import React, { useEffect, useMemo, useRef } from 'react';
import L from 'leaflet';
import 'leaflet.markercluster';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import { FLANDERS_BOUNDS, hasCoordinates } from './mapData';
import { buildPinCard, isPinCardExpanded, setPinCardExpanded, setPinCardPlanned } from './pinCard';
import { DEFAULT_MARKER_STYLE, MARKER_STYLE_DETAIL, pinIconHtml } from './markerStyle';
import './MachineMap.css';
import './PinCard.css';

// DJB-240: the Accounts › Maps map itself — OpenStreetMap tiles (free, no
// key) in a react-leaflet MapContainer, opened on all of Flanders, with every
// machine that has coordinates as a pin. Pins that sit too close together at
// the current zoom collapse into a leaflet.markercluster cluster that shows
// the machine count; zooming in (or clicking the cluster) splits it up.
//
// DJB-241: every pin carries an info card (pinCard.js) in a Leaflet popup.
// Desktop: hovering the pin opens the compact card, moving onto the card
// expands it (extra info + address → maps-app link); leaving closes it.
// Touch / click: tapping the pin opens the card expanded and keeps it open
// until the pin is tapped again or the map is tapped.
//
// DJB-242: the `machines` prop is already the filtered list (AccountMaps.js +
// mapFilters.js); the cluster layer rebuilds its markers on every change, so
// clusters and their counts follow the search / province / cash / days filters.
//
// DJB-243: `markerStyle` ('standard' | 'detail', markerStyle.js) picks the
// pin icon per machine. Standard = one blue pin for all; Detail = yellow + €
// glyph on a cash trigger, orange + clock glyph on a days trigger, blue
// otherwise. Switching rebuilds the cluster group's markers, so it is live.
//
// DJB-248 / DJB-249: the card's "Add to planning" pill calls `onAddToPlanning`
// and `plannedIds` keeps the pill state in sync (without rebuilding markers,
// so the open card stays open). `focusMachine` ({ machine, nonce }) is the
// panel's "Show in Maps": the map zooms to the pin, un-clusters it and opens
// its card expanded, as if it had been tapped.

const OSM_TILE_URL = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
const OSM_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

// CSS-drawn pin (MachineMap.css) instead of Leaflet's default PNG marker, so
// no bundler image-path shims are needed and the pin follows the theme accent.
// One L.divIcon per distinct HTML (icons are shared between markers).
const PIN_ICON_OPTIONS = {
  className: 'machine-pin-icon',
  iconSize: [24, 32],
  iconAnchor: [12, 30],
  tooltipAnchor: [0, -28],
};

const pinIconCache = new Map();

export const createPinIcon = (machine, markerStyle = DEFAULT_MARKER_STYLE) => {
  const html = pinIconHtml(machine, markerStyle);
  let icon = pinIconCache.get(html);
  if (!icon) {
    icon = L.divIcon({ ...PIN_ICON_OPTIONS, html });
    pinIconCache.set(html, icon);
  }
  return icon;
};

// Hover-close grace period: long enough to move the pointer from the pin onto
// the card (the address link must stay reachable), short enough that the
// card does not linger when the pointer sweeps past the pin.
export const CARD_CLOSE_DELAY_MS = 220;

export const createPinPopup = () =>
  L.popup({
    className: 'machine-pin-popup',
    closeButton: false,
    autoClose: true,
    closeOnClick: true,
    closeOnEscapeKey: true,
    autoPan: false,
    autoPanPadding: [16, 16],
    offset: [0, -26],
    minWidth: 216,
    maxWidth: 300,
  });

// Wires the card popup and its hover / tap state machine onto a marker.
export const attachPinCard = (marker, machine, options = {}) => {
  const card = buildPinCard(machine, options);
  const popup = createPinPopup().setContent(card);
  const { isPlanned } = options;
  const syncPlanned = () => {
    if (typeof isPlanned === 'function') setPinCardPlanned(card, isPlanned(machine.machineId));
  };
  let pinned = false;
  let closeTimer = null;
  let popupHooked = false;

  const clearCloseTimer = () => {
    if (closeTimer) {
      clearTimeout(closeTimer);
      closeTimer = null;
    }
  };

  const isOpen = () => Boolean(marker._map && marker._map.hasLayer(popup));

  const open = ({ expanded, pin }) => {
    clearCloseTimer();
    const map = marker._map;
    if (!map) return;
    pinned = pinned || Boolean(pin);
    setPinCardExpanded(card, expanded);
    syncPlanned();
    // only a deliberate tap/click may move the map; a hover never should
    popup.options.autoPan = pinned;
    if (!isOpen()) {
      popup.setLatLng(marker.getLatLng());
      map.openPopup(popup);
      hookPopupElement();
    } else if (pinned && popup._adjustPan) {
      popup._adjustPan();
    }
  };

  const close = () => {
    clearCloseTimer();
    pinned = false;
    setPinCardExpanded(card, false);
    if (isOpen()) {
      marker._map.closePopup(popup);
    }
  };

  const scheduleClose = () => {
    if (pinned) return;
    clearCloseTimer();
    closeTimer = setTimeout(() => {
      closeTimer = null;
      if (!pinned) close();
    }, CARD_CLOSE_DELAY_MS);
  };

  // the popup container is created on first open and reused afterwards
  const hookPopupElement = () => {
    if (popupHooked) return;
    const element = popup.getElement();
    if (!element) return;
    popupHooked = true;
    // native listeners: L.DomEvent rewrites mouseenter/leave into filtered
    // mouseover/out, which is not what we want on a container with children
    element.addEventListener('mouseenter', () => {
      clearCloseTimer();
      setPinCardExpanded(card, true);
    });
    element.addEventListener('mouseleave', () => {
      if (!pinned) {
        setPinCardExpanded(card, false);
        scheduleClose();
      }
    });
    // keep map clicks (which close the popup) from firing on the card itself
    L.DomEvent.disableClickPropagation(element);
  };

  // tap / click on the card body toggles the extra info (mobile); the
  // address link keeps working as a plain link and the planning pill
  // (DJB-248) handles its own click
  L.DomEvent.on(card, 'click', (event) => {
    if (event.target && event.target.closest && event.target.closest('a, button')) return;
    L.DomEvent.stop(event);
    pinned = true;
    setPinCardExpanded(card, !isPinCardExpanded(card));
  });

  marker.on('mouseover', () => {
    if (isOpen()) {
      clearCloseTimer();
      return;
    }
    open({ expanded: false, pin: false });
  });
  marker.on('mouseout', scheduleClose);
  // Leaflet closes an open popup on the map's synthetic 'preclick' before the
  // marker's own click fires; swallow it on the pin so a click on the pin can
  // toggle the card instead of close-then-reopen (same trick bindPopup uses).
  marker.on('preclick', L.DomEvent.stopPropagation);
  marker.on('click', (event) => {
    if (event && event.originalEvent) {
      L.DomEvent.stopPropagation(event.originalEvent);
    }
    if (pinned && isOpen()) {
      close();
      return;
    }
    open({ expanded: true, pin: true });
  });
  // the pin got clustered away / removed → the card must not float behind
  marker.on('remove', close);
  popup.on('remove', () => {
    clearCloseTimer();
    pinned = false;
    setPinCardExpanded(card, false);
  });

  marker.pinCard = card;
  marker.pinPopup = popup;
  // DJB-249 "Show in Maps": open the card as if the pin had been tapped
  marker.openPinCard = () => open({ expanded: true, pin: true });
  marker.syncPinCardPlanned = syncPlanned;
  return marker;
};

export const createMachineMarker = (machine, options = {}) => {
  const { markerStyle = DEFAULT_MARKER_STYLE, ...cardOptions } = options;
  const marker = L.marker([Number(machine.latitude), Number(machine.longitude)], {
    icon: createPinIcon(machine, markerStyle),
    title: machine.name || '',
    alt: machine.name || 'machine',
    keyboard: true,
    riseOnHover: true,
  });
  marker.machineId = machine.machineId;
  marker.markerStyle = markerStyle;
  return attachPinCard(marker, machine, cardOptions);
};

export const createClusterGroup = () =>
  L.markerClusterGroup({
    showCoverageOnHover: false,
    spiderfyOnMaxZoom: true,
    zoomToBoundsOnClick: true,
    maxClusterRadius: 60,
    // the default icon renders "<div><span>N</span></div>" with the count —
    // the size classes only recolour it (MachineMap.css)
  });

// "Show in Maps" zoom: street level, the pin is on its own and the card has
// room — but never zoom *out* if the operator is already closer.
export const FOCUS_ZOOM = 16;

export const findMarker = (group, machineId) => {
  if (!group || machineId == null) return null;
  const id = String(machineId);
  return group.getLayers().find((layer) => String(layer.machineId) === id) || null;
};

// Pan / zoom the map to a machine and open its card. Goes through the
// cluster group's zoomToShowLayer so a pin that sits inside a cluster (or in
// a spiderfied bunch at max zoom) is revealed first; a machine that is
// filtered off the map (no marker) still gets the map moved to its spot.
export const focusMachineOnMap = (map, group, machine) => {
  if (!map || !hasCoordinates(machine)) return false;
  const latLng = L.latLng(Number(machine.latitude), Number(machine.longitude));
  const marker = findMarker(group, machine.machineId);
  map.setView(latLng, Math.max(map.getZoom() || 0, FOCUS_ZOOM));
  if (marker) {
    group.zoomToShowLayer(marker, () => {
      if (marker.openPinCard) marker.openPinCard();
    });
  }
  return Boolean(marker);
};

// Inside the MapContainer: owns one markerClusterGroup for the map's lifetime
// and replaces its markers whenever the machine list or the pin style changes.
export const MachineClusterLayer = ({
  machines,
  markerStyle = DEFAULT_MARKER_STYLE,
  plannedIds,
  onAddToPlanning,
  focusMachine,
}) => {
  const map = useMap();
  const groupRef = useRef(null);
  // the card callbacks read the latest planning through a ref, so a planning
  // change never forces a marker rebuild (which would close the open card)
  const planningRef = useRef({ plannedIds, onAddToPlanning });
  planningRef.current = { plannedIds, onAddToPlanning };
  const cardOptions = useMemo(
    () => ({
      isPlanned: (machineId) => {
        const ids = planningRef.current.plannedIds;
        return Boolean(ids && ids.has && ids.has(String(machineId)));
      },
      onAddToPlanning: (machine) => {
        const add = planningRef.current.onAddToPlanning;
        if (typeof add === 'function') add(machine);
      },
    }),
    []
  );

  useEffect(() => {
    const group = createClusterGroup();
    groupRef.current = group;
    map.addLayer(group);
    return () => {
      map.removeLayer(group);
      groupRef.current = null;
    };
  }, [map]);

  // the pill only exists on maps that have a planning wired up
  const hasPlanning = typeof onAddToPlanning === 'function';

  useEffect(() => {
    const group = groupRef.current;
    if (!group) return;
    group.clearLayers();
    group.addLayers(
      (machines || []).map((machine) =>
        createMachineMarker(machine, hasPlanning ? { markerStyle, ...cardOptions } : { markerStyle })
      )
    );
  }, [machines, markerStyle, cardOptions, hasPlanning]);

  // planning changed → refresh the pill on every card (cheap: a class toggle)
  useEffect(() => {
    const group = groupRef.current;
    if (!group) return;
    group.getLayers().forEach((marker) => {
      if (marker.syncPinCardPlanned) marker.syncPinCardPlanned();
    });
  }, [plannedIds]);

  useEffect(() => {
    if (!focusMachine || !focusMachine.machine) return;
    focusMachineOnMap(map, groupRef.current, focusMachine.machine);
  }, [map, focusMachine]);

  return null;
};

// DJB-244: `.machine-map` is its own stacking context (MachineMap.css), so
// Leaflet's pane / control z-indexes (400–1000) stay inside the map box and
// the mobile nav overlay (z-index 200, MobileNavOverlay.css) slides over it.
const MachineMap = ({
  machines,
  markerStyle = DEFAULT_MARKER_STYLE,
  plannedIds,
  onAddToPlanning,
  focusMachine,
}) => (
  <div
    className={`machine-map${markerStyle === MARKER_STYLE_DETAIL ? ' is-detail-style' : ''}`}
    data-testid="machine-map"
    data-marker-style={markerStyle}
  >
    <MapContainer
      className="machine-map-canvas"
      bounds={FLANDERS_BOUNDS}
      boundsOptions={{ padding: [12, 12] }}
      minZoom={7}
      maxZoom={18}
      scrollWheelZoom
      attributionControl
    >
      <TileLayer url={OSM_TILE_URL} attribution={OSM_ATTRIBUTION} />
      <MachineClusterLayer
        machines={machines}
        markerStyle={markerStyle}
        plannedIds={plannedIds}
        onAddToPlanning={onAddToPlanning}
        focusMachine={focusMachine}
      />
    </MapContainer>
  </div>
);

export default MachineMap;
